import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  CurrencyRupeeIcon,
  DocumentIcon,
  BellIcon,
  GlobeAltIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from '../../utils/toast';

const TrademarkSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');
  const [uploadFields, setUploadFields] = useState([]);
  const [documentTemplates, setDocumentTemplates] = useState([]);

  useEffect(() => {
    loadSettings();
    loadUploadFields();
    loadDocumentTemplates();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trademark-applications/settings');
      setSettings(response.data.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load trademark settings');
    } finally {
      setLoading(false);
    }
  };

  const loadUploadFields = async () => {
    try {
      const response = await api.get('/admin/upload-fields');
      setUploadFields(response.data);
    } catch (error) {
      console.error('Failed to load upload fields:', error);
    }
  };

  const loadDocumentTemplates = async () => {
    try {
      const response = await api.get('/document-generation/templates?isActive=true');
      setDocumentTemplates(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load document templates:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put('/trademark-applications/settings', settings);
      setSettings(response.data.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePricing = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const updateDocumentRequirement = (docType, field, value) => {
    setSettings(prev => ({
      ...prev,
      documentRequirements: {
        ...prev.documentRequirements,
        [docType]: {
          ...prev.documentRequirements[docType],
          [field]: value
        }
      }
    }));
  };

  const updateWorkflow = (field, value) => {
    setSettings(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No settings found</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CogIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">Trademark Settings</h1>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'pricing', name: 'Pricing', icon: CurrencyRupeeIcon },
              { id: 'documents', name: 'Documents', icon: DocumentIcon },
              { id: 'workflow', name: 'Workflow', icon: CogIcon },
              { id: 'group-submission', name: 'Group Submission', icon: CheckCircleIcon },
              { id: 'invoice', name: 'Invoice', icon: DocumentIcon },
              { id: 'notifications', name: 'Notifications', icon: BellIcon },
              { id: 'integration', name: 'Integration', icon: GlobeAltIcon }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Pricing Configuration</h2>
              
              {/* International Pricing */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-md font-medium text-gray-900 mb-4">International (Non-India)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Government Fees
                    </label>
                    <input
                      type="number"
                      value={settings.pricing.international.governmentFees}
                      onChange={(e) => updatePricing('pricing.international.governmentFees', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Professional Fees
                    </label>
                    <input
                      type="number"
                      value={settings.pricing.international.professionalFees}
                      onChange={(e) => updatePricing('pricing.international.professionalFees', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* India Pricing */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900">India Pricing</h3>
                
                {/* Individual */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Individual</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Government Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.individual.governmentFees}
                        onChange={(e) => updatePricing('pricing.india.individual.governmentFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Professional Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.individual.professionalFees}
                        onChange={(e) => updatePricing('pricing.india.individual.professionalFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Small Enterprise */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Small Enterprise</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Government Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.smallEnterprise.governmentFees}
                        onChange={(e) => updatePricing('pricing.india.smallEnterprise.governmentFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Professional Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.smallEnterprise.professionalFees}
                        onChange={(e) => updatePricing('pricing.india.smallEnterprise.professionalFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udyog Aadhaar Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.smallEnterprise.udyogAadhaarFees}
                        onChange={(e) => updatePricing('pricing.india.smallEnterprise.udyogAadhaarFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Startup */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Startup</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Government Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.startup.governmentFees}
                        onChange={(e) => updatePricing('pricing.india.startup.governmentFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Professional Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.startup.professionalFees}
                        onChange={(e) => updatePricing('pricing.india.startup.professionalFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udyog Aadhaar Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.startup.udyogAadhaarFees}
                        onChange={(e) => updatePricing('pricing.india.startup.udyogAadhaarFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Startup Certificate Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.startup.startupCertificateFees}
                        onChange={(e) => updatePricing('pricing.india.startup.startupCertificateFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Others */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Others</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Government Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.others.governmentFees}
                        onChange={(e) => updatePricing('pricing.india.others.governmentFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Professional Fees
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.india.others.professionalFees}
                        onChange={(e) => updatePricing('pricing.india.others.professionalFees', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Services */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Services</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Udyog Aadhaar Assistance
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.udyogAadhaarAssistance}
                        onChange={(e) => updatePricing('pricing.udyogAadhaarAssistance', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Document Generation
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.documentGeneration}
                        onChange={(e) => updatePricing('pricing.documentGeneration', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Filing Assistance
                      </label>
                      <input
                        type="number"
                        value={settings.pricing.filingAssistance}
                        onChange={(e) => updatePricing('pricing.filingAssistance', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Turnover Threshold */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Turnover Threshold</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Threshold (in paise)
                    </label>
                    <input
                      type="number"
                      value={settings.pricing.turnoverThreshold}
                      onChange={(e) => updatePricing('pricing.turnoverThreshold', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="20000000 (2 CR)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter in paise (e.g., 20000000 for ₹2 CR)</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (  
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Document Requirements</h2>
              
              {/* Main Documents */}
              <div className="space-y-4">  
                <h3 className="text-md font-medium text-gray-900">Main Application Documents</h3>
                
                {['udyogAadhaar', 'startupCertificate', 'powerOfAttorney'].map(docType => (
                  <div key={docType} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 capitalize">
                      {docType.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`${docType}_required`}
                          checked={settings.documentRequirements[docType].required}
                          onChange={(e) => updateDocumentRequirement(docType, 'required', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${docType}_required`} className="ml-2 text-sm text-gray-700">
                          Required
                        </label>
                      </div> 
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`${docType}_autoTrigger`}
                          checked={settings.documentRequirements[docType].autoTrigger}
                          onChange={(e) => updateDocumentRequirement(docType, 'autoTrigger', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${docType}_autoTrigger`} className="ml-2 text-sm text-gray-700">
                          Auto-trigger
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`${docType}_createTask`}
                          checked={settings.documentRequirements[docType].createTask}
                          onChange={(e) => updateDocumentRequirement(docType, 'createTask', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`${docType}_createTask`} className="ml-2 text-sm text-gray-700">
                          Create Task
                        </label>
                      </div>
                    </div>
                    
                    {/* Field Mapping Configuration */}
                    <div className="border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Field Mapping</h5>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Upload Field
                        </label>
                        <select
                          value={settings.documentRequirements[docType].fieldMapping?.uploadFieldId || ''}
                          onChange={(e) => {
                            const selectedField = uploadFields.find(field => field._id === e.target.value);
                            setSettings(prev => ({
                              ...prev,
                              documentRequirements: {
                                ...prev.documentRequirements,
                                [docType]: {
                                  ...prev.documentRequirements[docType],
                                  fieldMapping: {
                                    uploadFieldId: e.target.value || null,
                                    masterDataField: selectedField?.name || docType
                                  }
                                }
                              }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select Upload Field</option>
                          {uploadFields.map(field => (
                            <option key={field._id} value={field._id}>
                              {field.label} ({field.name})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Select the upload field that contains this document in master data. 
                          The field name "{settings.documentRequirements[docType].fieldMapping?.masterDataField || docType}" will be used to find documents.
                        </p>
                      </div>
                      
                      {/* Document Generation Mapping */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Generation Template
                        </label>
                        <select
                          value={settings.documentRequirements[docType].documentGenerationMapping?.templateId || ''}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev,
                              documentRequirements: {
                                ...prev.documentRequirements,
                                [docType]: {
                                  ...prev.documentRequirements[docType],
                                  documentGenerationMapping: {
                                    templateId: e.target.value || null,
                                    enabled: e.target.value ? true : false
                                  }
                                }
                              }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select Document Template</option>
                          {documentTemplates.map(template => (
                            <option key={template._id} value={template._id}>
                              {template.name} ({template.category})
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Select a document generation template to automatically generate this document when needed.
                          Leave empty to disable automatic document generation.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trademark Documents */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900">Trademark Documents</h3>
                
                {['colorMark', 'logoMark', 'shape', 'sound', 'word'].map(docType => (
                  <div key={docType} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 capitalize">
                      {docType.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`trademark_${docType}_required`}
                        checked={settings.documentRequirements.trademarkDocuments[docType].required}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            documentRequirements: {
                              ...prev.documentRequirements,
                              trademarkDocuments: {
                                ...prev.documentRequirements.trademarkDocuments,
                                [docType]: {
                                  ...prev.documentRequirements.trademarkDocuments[docType],
                                  required: e.target.checked
                                }
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor={`trademark_${docType}_required`} className="ml-2 text-sm text-gray-700">
                        Required for this trademark type
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Workflow Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto Invoice Generation</h4>
                    <p className="text-sm text-gray-500">Automatically generate invoices when applications are submitted</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.workflow.autoInvoice}
                    onChange={(e) => updateWorkflow('autoInvoice', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto Document Trigger</h4>
                    <p className="text-sm text-gray-500">Automatically trigger document generation for missing documents</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.workflow.autoDocumentTrigger}
                    onChange={(e) => updateWorkflow('autoDocumentTrigger', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto Task Creation</h4>
                    <p className="text-sm text-gray-500">Automatically create tasks for admin team when assistance is needed</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.workflow.autoTaskCreation}
                    onChange={(e) => updateWorkflow('autoTaskCreation', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Public Sharing</h4>
                    <p className="text-sm text-gray-500">Enable public sharing links for trademark applications</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.workflow.publicSharing}
                    onChange={(e) => updateWorkflow('publicSharing', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Draft Expiry</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Days
                    </label>
                    <input
                      type="number"
                      value={settings.workflow.draftExpiryDays}
                      onChange={(e) => updateWorkflow('draftExpiryDays', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of days before draft applications expire</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'group-submission' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Group Submission Settings</h2>
              <p className="text-sm text-gray-600 mb-6">
                Configure what happens after trademark submission for each group. You can set different actions for different groups.
              </p>
              
              <div className="space-y-6">
                {settings.groups?.map((group) => {
                  const groupSettings = settings.groupSubmissionSettings?.find(
                    gs => gs.groupId === group._id
                  ) || {
                    groupId: group._id,
                    groupName: group.name,
                    submissionAction: 'generate_invoice',
                    autoGenerateInvoice: true,
                    redirectToPayment: false,
                    isActive: true
                  };

                  return (
                    <div key={group._id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                          {group.description && (
                            <p className="text-sm text-gray-500">{group.description}</p>
                          )}
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={groupSettings.isActive}
                            onChange={(e) => {
                              const newSettings = { ...settings };
                              const existingIndex = newSettings.groupSubmissionSettings?.findIndex(
                                gs => gs.groupId === group._id
                              );
                              
                              if (existingIndex >= 0) {
                                newSettings.groupSubmissionSettings[existingIndex].isActive = e.target.checked;
                              } else {
                                if (!newSettings.groupSubmissionSettings) {
                                  newSettings.groupSubmissionSettings = [];
                                }
                                newSettings.groupSubmissionSettings.push({
                                  groupId: group._id,
                                  groupName: group.name,
                                  submissionAction: 'generate_invoice',
                                  autoGenerateInvoice: true,
                                  redirectToPayment: false,
                                  isActive: e.target.checked
                                });
                              }
                              setSettings(newSettings);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Active</span>
                        </div>
                      </div>

                      {groupSettings.isActive && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Submission Action
                            </label>
                            <select
                              value={groupSettings.submissionAction}
                              onChange={(e) => {
                                const newSettings = { ...settings };
                                const existingIndex = newSettings.groupSubmissionSettings?.findIndex(
                                  gs => gs.groupId === group._id
                                );
                                
                                if (existingIndex >= 0) {
                                  newSettings.groupSubmissionSettings[existingIndex].submissionAction = e.target.value;
                                  // Update related fields based on action
                                  if (e.target.value === 'generate_invoice') {
                                    newSettings.groupSubmissionSettings[existingIndex].autoGenerateInvoice = true;
                                    newSettings.groupSubmissionSettings[existingIndex].redirectToPayment = false;
                                  } else if (e.target.value === 'generate_invoice_and_redirect') {
                                    newSettings.groupSubmissionSettings[existingIndex].autoGenerateInvoice = true;
                                    newSettings.groupSubmissionSettings[existingIndex].redirectToPayment = true;
                                  } else if (e.target.value === 'submit_only') {
                                    newSettings.groupSubmissionSettings[existingIndex].autoGenerateInvoice = false;
                                    newSettings.groupSubmissionSettings[existingIndex].redirectToPayment = false;
                                  }
                                } else {
                                  if (!newSettings.groupSubmissionSettings) {
                                    newSettings.groupSubmissionSettings = [];
                                  }
                                  newSettings.groupSubmissionSettings.push({
                                    groupId: group._id,
                                    groupName: group.name,
                                    submissionAction: e.target.value,
                                    autoGenerateInvoice: e.target.value !== 'submit_only',
                                    redirectToPayment: e.target.value === 'generate_invoice_and_redirect',
                                    isActive: true
                                  });
                                }
                                setSettings(newSettings);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="generate_invoice">Show Invoice on Page</option>
                              <option value="generate_invoice_and_redirect">Show Invoice with Payment Options</option>
                              <option value="submit_only">Submit Form Only</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              {groupSettings.submissionAction === 'generate_invoice' && 
                                'Display invoice on the same page after submission'}
                              {groupSettings.submissionAction === 'generate_invoice_and_redirect' && 
                                'Display invoice with payment options on the same page'}
                              {groupSettings.submissionAction === 'submit_only' && 
                                'Only submit the form without showing invoice'}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">Auto Generate Invoice</h4>
                                <p className="text-sm text-gray-500">Automatically generate invoice after submission</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={groupSettings.autoGenerateInvoice}
                                onChange={(e) => {
                                  const newSettings = { ...settings };
                                  const existingIndex = newSettings.groupSubmissionSettings?.findIndex(
                                    gs => gs.groupId === group._id
                                  );
                                  
                                  if (existingIndex >= 0) {
                                    newSettings.groupSubmissionSettings[existingIndex].autoGenerateInvoice = e.target.checked;
                                  } else {
                                    if (!newSettings.groupSubmissionSettings) {
                                      newSettings.groupSubmissionSettings = [];
                                    }
                                    newSettings.groupSubmissionSettings.push({
                                      groupId: group._id,
                                      groupName: group.name,
                                      submissionAction: 'generate_invoice',
                                      autoGenerateInvoice: e.target.checked,
                                      redirectToPayment: false,
                                      isActive: true
                                    });
                                  }
                                  setSettings(newSettings);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </div>

                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">Redirect to Payment</h4>
                                <p className="text-sm text-gray-500">Redirect user to payment page after submission</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={groupSettings.redirectToPayment}
                                onChange={(e) => {
                                  const newSettings = { ...settings };
                                  const existingIndex = newSettings.groupSubmissionSettings?.findIndex(
                                    gs => gs.groupId === group._id
                                  );
                                  
                                  if (existingIndex >= 0) {
                                    newSettings.groupSubmissionSettings[existingIndex].redirectToPayment = e.target.checked;
                                  } else {
                                    if (!newSettings.groupSubmissionSettings) {
                                      newSettings.groupSubmissionSettings = [];
                                    }
                                    newSettings.groupSubmissionSettings.push({
                                      groupId: group._id,
                                      groupName: group.name,
                                      submissionAction: 'generate_invoice',
                                      autoGenerateInvoice: true,
                                      redirectToPayment: e.target.checked,
                                      isActive: true
                                    });
                                  }
                                  setSettings(newSettings);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'invoice' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Invoice Settings</h2>
              <p className="text-sm text-gray-600 mb-6">
                Configure company information that will appear on all generated invoices.
              </p>
              
              <div className="space-y-6">
                {/* Company Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={settings.invoiceIssuer?.companyName || ''}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            invoiceIssuer: {
                              ...prev.invoiceIssuer,
                              companyName: e.target.value
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={settings.invoiceIssuer?.email || ''}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            invoiceIssuer: {
                              ...prev.invoiceIssuer,
                              email: e.target.value
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="info@yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone *
                      </label>
                      <input
                        type="text"
                        value={settings.invoiceIssuer?.phone || ''}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            invoiceIssuer: {
                              ...prev.invoiceIssuer,
                              phone: e.target.value
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4">Address</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Address *
                    </label>
                    <textarea
                      value={settings.invoiceIssuer?.address || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          invoiceIssuer: {
                            ...prev.invoiceIssuer,
                            address: e.target.value
                          }
                        }));
                      }}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter your complete company address"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Notification Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email Notifications */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900">Email Notifications</h3>
                  
                  {Object.keys(settings.notifications.email).map(notification => (
                    <div key={notification} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 capitalize">
                          {notification.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.email[notification]}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            notifications: {
                              ...prev.notifications,
                              email: {
                                ...prev.notifications.email,
                                [notification]: e.target.checked
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>

                {/* SMS Notifications */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-gray-900">SMS Notifications</h3>
                  
                  {Object.keys(settings.notifications.sms).map(notification => (
                    <div key={notification} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 capitalize">
                          {notification.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.sms[notification]}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            notifications: {
                              ...prev.notifications,
                              sms: {
                                ...prev.notifications.sms,
                                [notification]: e.target.checked
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integration' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Integration Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">IPINDIA Integration</h4>
                    <p className="text-sm text-gray-500">Enable integration with IPINDIA for automatic filing</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.ipindiaIntegration.enabled}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        ipindiaIntegration: {
                          ...prev.ipindiaIntegration,
                          enabled: e.target.checked
                        }
                      }));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                {settings.ipindiaIntegration.enabled && (
                  <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Endpoint
                      </label>
                      <input
                        type="text"
                        value={settings.ipindiaIntegration.apiEndpoint || ''}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            ipindiaIntegration: {
                              ...prev.ipindiaIntegration,
                              apiEndpoint: e.target.value
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="https://api.ipindia.gov.in"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key
                      </label>
                      <input
                        type="password"
                        value={settings.ipindiaIntegration.apiKey || ''}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            ipindiaIntegration: {
                              ...prev.ipindiaIntegration,
                              apiKey: e.target.value
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Enter API key"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoFiling"
                        checked={settings.ipindiaIntegration.autoFiling}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            ipindiaIntegration: {
                              ...prev.ipindiaIntegration,
                              autoFiling: e.target.checked
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="autoFiling" className="ml-2 text-sm text-gray-700">
                        Enable automatic filing
                      </label>
                    </div>
                  </div>
                )}

                {/* GPT Integration */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">GPT Integration</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="gptEnabled"
                        checked={settings.fieldMapping.gptIntegration.enabled}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            fieldMapping: {
                              ...prev.fieldMapping,
                              gptIntegration: {
                                ...prev.fieldMapping.gptIntegration,
                                enabled: e.target.checked
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="gptEnabled" className="ml-2 text-sm text-gray-700">
                        Enable GPT for auto-fill descriptions
                      </label>
                    </div>
                    {settings.fieldMapping.gptIntegration.enabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <input
                          type="password"
                          value={settings.fieldMapping.gptIntegration.apiKey || ''}
                          onChange={(e) => {
                            setSettings(prev => ({
                              ...prev,
                              fieldMapping: {
                                ...prev.fieldMapping,
                                gptIntegration: {
                                  ...prev.fieldMapping.gptIntegration,
                                  apiKey: e.target.value
                                }
                              }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Enter OpenAI API key"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrademarkSettings; 